import { Box, useTheme, Button, Chip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import { LockOutlined, LockOpenOutlined } from "@mui/icons-material";
import Header from "../../components/Header";
import { db, collection, getDocs } from "../../firebase";
import { useEffect, useState } from "react";
import { doc, updateDoc } from "firebase/firestore";

const User = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "user")); // Fetch data from 'users' collection
        const userData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setRows(userData);
      } catch (error) {
        console.error("Error fetching data: ", error);
      }
    };

    fetchData();
  }, []);

  const toggleBlockStatus = async (userId, isBlocked) => {
    try {
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, { isBlocked: !isBlocked });

      // Update UI immediately
      setRows((prevRows) =>
        prevRows.map((user) =>
          user.id === userId ? { ...user, isBlocked: !isBlocked } : user
        )
      );
    } catch (error) {
      console.error("Error updating block status:", error);
    }
  };

  const columns = [
    {
      field: "serialNumber",
      headerName: "S.No",
      width: 80,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => params.api.getRowIndex(params.id) + 1, // Generate serial number
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1.5,
      cellClassName: "name-column--cell",
    },
    {
      field: "dob",
      headerName: "Age",
      type: "number",
      headerAlign: "center",
      align: "center",
      renderCell: ({ value }) => {
        if (!value) return "N/A";
        const [day, month, year] = value.split("/").map(Number);
        const dobDate = new Date(year, month - 1, day);
        const today = new Date();
        let age = today.getFullYear() - dobDate.getFullYear();
        if (
          today.getMonth() < dobDate.getMonth() ||
          (today.getMonth() === dobDate.getMonth() && today.getDate() < dobDate.getDate())
        ) {
          age--;
        }
        return age;
      },
    },
    {
      field: "phone",
      headerName: "Phone",
      flex: 1,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.5,
    },
    {
      field: "isBlocked",
      headerName: "Status",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: ({ row }) => (
        <Chip
          label={row.isBlocked ? "Blocked" : "Active"}
          color={row.isBlocked ? "error" : "success"}
          variant="outlined"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: ({ row }) => (
        <Button
          variant="contained"
          size="small"
          color={row.isBlocked ? "error" : "success"}
          startIcon={row.isBlocked ? <LockOutlined /> : <LockOpenOutlined />}
          onClick={() => toggleBlockStatus(row.id, row.isBlocked)}
        >
          {row.isBlocked ? "Unblock" : "Block"}
        </Button>
      ),
    },
  ];

  return (
    <Box m="20px">
      <Header title="USER MANAGEMENT" subtitle="Manage Users Efficiently" />
      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
            fontSize: "14px",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
            fontSize: "14px",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
            fontWeight: "bold",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            color: "#fff",
            fontWeight: "bold",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
            color: "#fff",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: colors.primary[600],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        <DataGrid
          checkboxSelection
          rows={rows}
          columns={columns}
          getRowHeight={() => 55} // Increased row height for better spacing
        />
      </Box>
    </Box>
  );
};

export default User;
